import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { resolveCurrentUserId } from "@/lib/articulos.server";
import { SEO_ORIGIN } from "@/lib/seo";

/**
 * Cliente de servicio: selecciones no tiene políticas RLS y la página pública
 * es anónima. Los permisos se comprueban en el código antes de usarlo.
 */
async function servicio() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

/**
 * Selecciones de locales: el agente escoge locales de las coincidencias de una
 * demanda y se los envía al cliente como una página temporal con su perfil
 * (por email, WhatsApp o enlace). Caducan a los 30 días.
 */

async function assertPuedeGestionarDemanda(userId: string, demandaId: string) {
  const sb = await servicio();
  const [{ data: roles }, { data: members }, { data: dem }] = await Promise.all([
    sb.from("user_roles").select("role").eq("user_id", userId),
    sb.from("agencia_miembros").select("agencia_id").eq("user_id", userId),
    sb.from("demandas").select("id, contacto_id, agencia_id").eq("id", demandaId).maybeSingle(),
  ]);
  if (!dem) throw new Error("Demanda no encontrada");
  const rs = (roles ?? []).map((r: { role: string }) => r.role);
  const isStaff = rs.includes("admin") || rs.includes("customer_service") || rs.includes("agente");
  const agenciaIds = (members ?? []).map((m: { agencia_id: string }) => m.agencia_id);
  if (!isStaff && !(dem.agencia_id && agenciaIds.includes(dem.agencia_id))) throw new Error("No autorizado");
  return dem as { id: string; contacto_id: string; agencia_id: string | null };
}

function nuevoToken() {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const nombreDe = (c: { nombre?: string | null; apellidos?: string | null } | null) =>
  c ? [c.nombre, c.apellidos].filter(Boolean).join(" ").trim() : "";

export const crearSeleccion = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        demanda_id: z.string().uuid(),
        anuncio_ids: z.array(z.string().uuid()).min(1).max(30),
        mensaje: z.string().trim().max(2000).nullable().optional(),
        canal: z.enum(["email", "whatsapp", "enlace"]),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Unauthorized");
    const dem = await assertPuedeGestionarDemanda(userId, data.demanda_id);
    const sb = await servicio();

    const [{ data: cliente }, { data: agente }, { data: anuncios }] = await Promise.all([
      sb.from("contactos").select("id, nombre, apellidos, email, telefono").eq("id", dem.contacto_id).maybeSingle(),
      sb.from("contactos").select("nombre, apellidos").eq("user_id", userId).maybeSingle(),
      sb
        .from("anuncios")
        .select("id")
        .in("id", data.anuncio_ids)
        .eq("estado", "publicado")
        .eq("visibilidad", "publico"),
    ]);
    if (!cliente) throw new Error("La demanda no tiene contacto");
    // Solo locales públicos: la página del cliente no muestra el resto.
    const publicos = new Set((anuncios ?? []).map((a: { id: string }) => a.id));
    const ids = data.anuncio_ids.filter((id) => publicos.has(id));
    if (ids.length === 0) throw new Error("Ninguno de los locales elegidos está publicado como público");
    if (data.canal === "email" && !cliente.email) throw new Error("El contacto no tiene email");
    if (data.canal === "whatsapp" && !cliente.telefono) throw new Error("El contacto no tiene teléfono");

    let agenciaNombre = "";
    if (dem.agencia_id) {
      const { data: ag } = await sb.from("agencias").select("nombre").eq("id", dem.agencia_id).maybeSingle();
      agenciaNombre = (ag as { nombre?: string } | null)?.nombre ?? "";
    }

    const token = nuevoToken();
    const { data: sel, error } = await sb
      .from("selecciones")
      .insert({
        token,
        demanda_id: dem.id,
        contacto_id: cliente.id,
        agente_id: userId,
        agencia_id: dem.agencia_id,
        anuncio_ids: ids,
        mensaje: data.mensaje ?? null,
        canal: data.canal,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);

    const url = `${SEO_ORIGIN}/seleccion/${token}`;
    const agenteNombre = nombreDe(agente) || "Tu agente";
    const saludo = cliente.nombre ? `Hola ${cliente.nombre},` : "Hola,";
    const mensaje = data.mensaje?.trim() || "Te paso los locales que mejor encajan con lo que buscas.";
    const meta = { tipo: "seleccion_locales", seleccion_id: sel.id, anuncio_ids: ids, demanda_id: dem.id };

    if (data.canal === "email") {
      const { sendLoggedManagedEmail } = await import("@/lib/logged-email.server");
      await sendLoggedManagedEmail(sb as never, {
        contactId: cliente.id,
        to: cliente.email!,
        actor: userId,
        tipo: "seleccion_locales",
        meta,
        draft: {
          key: "seleccion_locales",
          variables: {
            saludo,
            mensaje,
            num_locales: String(ids.length),
            agente_nombre: agenteNombre,
            agencia_nombre: agenciaNombre,
            seleccion_url: url,
          },
        },
      });
      return { ok: true as const, url, whatsappUrl: null };
    }

    if (data.canal === "whatsapp") {
      const texto = `${saludo} ${mensaje}\n\nHe escogido ${ids.length} locales para ti: ${url}\n\n${agenteNombre}`;
      await sb.from("comunicaciones").insert({
        channel: "whatsapp",
        direction: "outbound",
        status: "enviado",
        contact_id: cliente.id,
        body: texto,
        meta_data: { ...meta, enviado_por: userId },
      });
      const digitos = (cliente.telefono ?? "").replace(/\D/g, "");
      const tel = digitos.length === 9 ? `34${digitos}` : digitos;
      return { ok: true as const, url, whatsappUrl: `https://wa.me/${tel}?text=${encodeURIComponent(texto)}` };
    }

    return { ok: true as const, url, whatsappUrl: null };
  });

export const listSeleccionesDemanda = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ demanda_id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Unauthorized");
    await assertPuedeGestionarDemanda(userId, data.demanda_id);
    const sb = await servicio();
    const { data: rows, error } = await sb
      .from("selecciones")
      .select("id, token, canal, anuncio_ids, anuncios_vistos, aperturas, primera_apertura_at, ultima_apertura_at, expires_at, created_at")
      .eq("demanda_id", data.demanda_id)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (rows ?? []).map((r) => ({ ...r, url: `${SEO_ORIGIN}/seleccion/${r.token}` }));
  });

const TokenInput = z.object({ token: z.string().trim().min(8).max(40).regex(/^[A-Za-z0-9_-]+$/) });

/** Página pública de la selección: datos del agente y locales elegidos. */
export const getSeleccionPublica = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => TokenInput.parse(d))
  .handler(async ({ data }) => {
    const sb = await servicio();
    const { data: sel } = await sb
      .from("selecciones")
      .select("id, agente_id, agencia_id, contacto_id, anuncio_ids, mensaje, expires_at")
      .eq("token", data.token)
      .maybeSingle();
    if (!sel) return null;
    const caducada = new Date(sel.expires_at).getTime() < Date.now();

    const [{ data: agente }, { data: cliente }, agRes] = await Promise.all([
      sb
        .from("contactos")
        .select("id, slug, nombre, apellidos, email, telefono, bio, foto_url, cargos(nombre)")
        .eq("user_id", sel.agente_id)
        .maybeSingle(),
      sb.from("contactos").select("nombre").eq("id", sel.contacto_id).maybeSingle(),
      sel.agencia_id
        ? sb.from("agencias").select("id, slug, nombre, logo_url").eq("id", sel.agencia_id).maybeSingle()
        : Promise.resolve({ data: null }),
    ]);
    const a = agente as
      | {
          id: string;
          slug: string | null;
          nombre: string;
          apellidos: string | null;
          email: string | null;
          telefono: string | null;
          bio: string | null;
          foto_url: string | null;
          cargos?: { nombre: string } | null;
        }
      | null;
    const ag = (agRes as { data: { id: string; slug: string | null; nombre: string; logo_url: string | null } | null })
      .data;
    return {
      caducada,
      clienteNombre: (cliente as { nombre?: string | null } | null)?.nombre ?? null,
      mensaje: sel.mensaje as string | null,
      anuncioIds: caducada ? [] : (sel.anuncio_ids as string[]),
      agente: {
        id: a?.id ?? "",
        user_id: sel.agente_id as string,
        slug: a?.slug ?? "",
        nombre: a?.nombre ?? "Tu agente",
        apellidos: a?.apellidos ?? null,
        email: a?.email ?? null,
        telefono: a?.telefono ?? null,
        bio: null,
        foto_url: a?.foto_url ?? null,
        cargo: a?.cargos?.nombre ?? null,
        agencia: ag ? { id: ag.id, slug: ag.slug ?? null, nombre: ag.nombre, logo_url: ag.logo_url ?? null } : null,
        anuncios_asignados_count: 0,
      },
    };
  });

/** Registra que el cliente abrió la selección o una de sus fichas. */
export const registrarVistaSeleccion = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => TokenInput.extend({ anuncio_id: z.string().uuid().optional() }).parse(d))
  .handler(async ({ data }) => {
    const sb = await servicio();
    const { data: sel } = await sb
      .from("selecciones")
      .select("id, anuncio_ids, anuncios_vistos, aperturas, primera_apertura_at, expires_at")
      .eq("token", data.token)
      .maybeSingle();
    if (!sel || new Date(sel.expires_at).getTime() < Date.now()) return { ok: false as const };
    const now = new Date().toISOString();
    if (data.anuncio_id) {
      const vistos = (sel.anuncios_vistos ?? []) as string[];
      if (!(sel.anuncio_ids as string[]).includes(data.anuncio_id) || vistos.includes(data.anuncio_id)) {
        return { ok: true as const };
      }
      await sb.from("selecciones").update({ anuncios_vistos: [...vistos, data.anuncio_id] }).eq("id", sel.id);
      return { ok: true as const };
    }
    await sb
      .from("selecciones")
      .update({
        aperturas: (sel.aperturas ?? 0) + 1,
        primera_apertura_at: sel.primera_apertura_at ?? now,
        ultima_apertura_at: now,
      })
      .eq("id", sel.id);
    return { ok: true as const };
  });
